import { Subscription } from 'rxjs';
import { LayerData } from 'src/app/data/configurator/layer/layer.data';
import { LabelEvent } from '../events/label.event';
import { ContextData } from 'src/app/data/context.data';
import { CommandData } from 'src/app/data/configurator/command.data';
import { HistoryData } from 'src/app/data/configurator/history.data';
import { LabelData } from 'src/app/data/configurator/label/label.data';

declare const fabric: any;

export class AbstractLayer {

  // data
  protected _stage: any;
  protected _data: LayerData;
  protected _labelData: LabelData;
  protected _positionData: Object;

  protected _display:any;
  protected _preview:any;

  protected _currentCanvasAngle: number = 0;

  protected _subscription: Subscription;

  /**
   * Contructor
   */
  constructor(data: LayerData, stage:any) {
    this._data = data;
    this._stage = stage;

    this._subscription = this._data.onChange.subscribe({
      next: (event: LabelEvent) => { this._display && this._preview && this.updateView() }
    });
  }

  /**
   * Draw
   */
  public draw(): Promise<boolean> {

    return new Promise((resolve, reject) => {
      this._draw();
      resolve(true);
    });
  }

  protected _draw(): any {

    this._display.set({
      data: this._data,
      padding: 0,
      strokeWidth: 0
    });

    this._preview.data = this._data;

    this._display.on('modified', (e) => {
      this.updateData(this._display);
    });

    this._display.hasRotatingPoint = !this._data.locks.rotation;
    if (this._data.locks.size) {
        this._display.setControlsVisibility({
          bl: false,
          br: false,
          mb: false,
          ml: false,
          mr: false,
          mt: false,
          tl: false,
          tr: false,
          mtr: false,
        })
    }

    this._display.lockMovementX = this._data.locks.position;
    this._display.lockMovementY = this._data.locks.position;
    this._display.lockScalingX = this._data.locks.size;
    this._display.lockScalingY = this._data.locks.size;
    this._display.lockRotation = this._data.locks.rotation;

    this.updateView();

  }

  /**
   * Update data
   */
  updateData(object:any):void {
    // console.log("AbstractLayer.updateData()");

    if (this._currentCanvasAngle % 360 == 0) {
      this._data.y = object.top;
      this._data.x = object.left;
    } else if (this._currentCanvasAngle % 270 == 0) {
      this._data.x = -object.top;
      this._data.y = object.left;
    } else if (this._currentCanvasAngle % 180 == 0) {
      this._data.y = -object.top;
      this._data.x = -object.left;
    } else if (this._currentCanvasAngle % 90 == 0) {
      this._data.x = object.top;
      this._data.y = -object.left;
    }


    this._data.rotation = object.angle - this._currentCanvasAngle;

    this._data.width = object.width * object.scaleX;
    this._data.height = object.height * object.scaleY;

    this._preview.left = this._data.x;
    this._preview.top = this._data.y;

    this._preview.width = object.width;
    this._preview.height = object.height;

    this._preview.scaleX = this._data.width / object.width;
    this._preview.scaleY = this._data.height / object.height;
    this._preview.angle = this._data.rotation;

    this._data.invalidate();
  }

  /**
   * Update view
   */
  updateView(): void {

    // console.log("AbstractLayer.updateView()");

    this._display.angle = this._currentCanvasAngle + this._data.rotation;
    this._preview.angle = this._data.rotation;

    if (!isNaN(this._data.width) && isNaN(this._data.height)) {
      this._display.scaleX = this._data.width / this._display.width;
      this._display.scaleY = this._display.scaleX;
      this._data.height = this._display.height * this._display.scaleX;
    } else if (isNaN(this._data.width) && !isNaN(this._data.height)) {
      this._display.scaleY = this._data.height / this._display.height;
      this._display.scaleX = this._display.scaleY;
      this._data.width = this._display.width * this._display.scaleY;
    } else if (isNaN(this._data.width) && isNaN(this._data.height)) {
      this._display.scaleY = ContextData.CONVERT_72_TO_DPI;
      this._display.scaleX = ContextData.CONVERT_72_TO_DPI;
    }

    if (!isNaN(this._data.width)) {
      this._display.scaleX = this._data.width / this._display.width;
    } else {
      this._data.width = this._display.width * this._display.scaleX;
    }

    if (!isNaN(this._data.height)) {
      this._display.scaleY = this._data.height / this._display.height;;
    } else {
      this._data.height = this._display.height * this._display.scaleY;
    }

    this._preview.scaleX = this._display.scaleX;
    this._preview.scaleY = this._display.scaleY;

    this.updatePosition();
  }

  /**
   * Display
   */
  get display():any {
    return this._display;
  }

  /**
   * preview
   */
  get preview(): any {
    return this._preview;
  }

  /**
   * Angle
   */
  set currentCanvasAngle(value:number) {

    this._currentCanvasAngle = value;

    if (this._display) {
      this._display.angle = value + this._data.rotation;

      if (this._preview) {
        this._preview.angle = this._data.rotation;
      }

      this.updatePosition();
    } else {

    }


  }

  /**
   * Label
   */
  set labelData(value: LabelData) {
    this._labelData = value;
  }

  protected updatePosition():void {
    // console.log("AbstractLayer.updatePosition()")
    let t: number;
    let l: number;
    if (this._currentCanvasAngle % 360 == 0) {
      t = this._data.y;
      l = this._data.x;
    } else if (this._currentCanvasAngle % 270 == 0) {
      t = -this._data.x;
      l = this._data.y;
    } else if (this._currentCanvasAngle % 180 == 0) {
      t = -this._data.y;
      l = -this._data.x;
    } else if (this._currentCanvasAngle % 90 == 0) {
      t = this._data.x;
      l = -this._data.y;
    }



    this._display.set({
      top: t,
      left: l
    });

    this._display.setCoords();


    if(!this._preview) return;

    this._preview.set({
      top: this._data.y,
      left: this._data.x
    });

    this._preview.setCoords();
  }

  /**
   * Stage
   */
  set stage(value: any) {
    this._stage = value;
  }

  /**
   * Data
   */
  get data():LayerData {
    return this._data;
  }

  /**
   * Cleanup
   */
  public cleanup(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    if (this._display) {
      this._display.off('modified');
    }
  }
}
